import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Video, ArrowRight, Mail, Lock, User } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_screen-studio-3/artifacts/rr2m67nx_logo%20valnexo%20vectorise%CC%81.png";

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Connexion réussie !');
      } else {
        await register(formData.name, formData.email, formData.password);
        toast.success('Compte créé avec succès !');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 50%, #0F172A 100%)'
        }}
      >
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-64 h-64 bg-blue-500 rounded-full blur-3xl" />
          <div className="absolute bottom-40 right-20 w-96 h-96 bg-blue-600 rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <div className="flex items-center gap-3 mb-8">
            <img src={LOGO_URL} alt="Valnexo" className="w-12 h-12 object-contain" />
            <span className="text-2xl font-semibold font-['Outfit']">Valnexo Academy Studio</span>
          </div>
          
          <h1 className="text-4xl lg:text-5xl font-semibold font-['Outfit'] leading-tight mb-6">
            Créez des formations <br />
            <span className="text-blue-400">professionnelles</span> en quelques clics
          </h1>
          
          <p className="text-lg text-slate-300 mb-8 max-w-md">
            Studio de production vidéo, gestion de formations, distribution — tout ce dont vous avez besoin pour former vos équipes.
          </p>
          
          <div className="flex flex-col gap-4">
            {[
              'Capture écran + caméra HD',
              'Organisation par modules',
              'Publication YouTube automatique',
              'Analytics en temps réel'
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3 text-slate-200">
                <div className="w-5 h-5 rounded-full bg-blue-600/30 flex items-center justify-center">
                  <ArrowRight className="w-3 h-3 text-blue-400" />
                </div>
                {feature}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <img src={LOGO_URL} alt="Valnexo" className="w-10 h-10 object-contain" />
            <span className="text-xl font-semibold font-['Outfit'] text-slate-900">Valnexo Academy Studio</span>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
            <h2 className="text-2xl font-semibold font-['Outfit'] text-slate-900 mb-2">
              {isLogin ? 'Content de vous revoir' : 'Créer votre compte'}
            </h2>
            <p className="text-slate-500 mb-6">
              {isLogin 
                ? 'Connectez-vous pour accéder à votre espace' 
                : 'Commencez à créer des formations professionnelles'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-700">Nom complet</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="name"
                      data-testid="register-name-input"
                      type="text"
                      placeholder="Jean Dupont"
                      className="pl-10"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required={!isLogin}
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    data-testid="auth-email-input"
                    type="email"
                    placeholder="vous@entreprise.com"
                    className="pl-10"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    data-testid="auth-password-input"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="auth-submit-btn"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium"
                disabled={loading}
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : isLogin ? (
                  'Se connecter'
                ) : (
                  'Créer mon compte'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                data-testid="auth-toggle-btn"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-slate-600 hover:text-blue-600 transition-colors"
              >
                {isLogin ? (
                  <>Pas encore de compte ? <span className="font-medium text-blue-600">S'inscrire</span></>
                ) : (
                  <>Déjà un compte ? <span className="font-medium text-blue-600">Se connecter</span></>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
